<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinColumns } from "~/modules/dtwin/models/DtwinColumns.enum";
import { DtwinListDisplayContext } from "~/modules/dtwin/models/DtwinListDisplayContext.enum";
import { useGetFleet } from "~/modules/fleet/composables/useGetFleet";
import { useUpdateFleet } from "~/modules/fleet/composables/useUpdateFleet";
import { PATH_FLEETS } from "~/modules/fleet/constants";
import { FleetUpdateActionType } from "~/modules/fleet/models/FleetUpdateActionType.enum";
import type { FleetUpdatePayload } from "~/modules/fleet/models/FleetUpdatePayload.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";
import ILxcPlus from "~icons/lxc/plus";

const acl = useAcl();
const { t } = useI18n();
const route = useRoute();

const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  getFleet,
  result: resultGetFleet,
  isLoading: isLoadingGetFleet,
  error: errorGetFleet,
} = useGetFleet({
  organizationCode: currentOrganization.value.code,
});

const {
  updateFleet,
  isLoading: isLoadingUpdateFleet,
  error: errorUpdateFleet,
} = useUpdateFleet({
  organizationCode: currentOrganization.value.code,
});

const isAllowedToManageFleet = computed(() =>
  acl.can(ACLRoles.CAN_WRITE_DEVICE),
);
const reloadDtwins: Ref<boolean> = ref(false);
const reloadAttachDtwinsList: Ref<boolean> = ref(false);
const attachDevicesPanelShown: Ref<boolean> = ref(false);
const dtwinsListRef = ref();
const showConfirmRemoveDevices: Ref<boolean> = ref(false);
const lxcFleetAttachDevicesRef = ref();
const selectedDtwins: Ref<Dtwin[]> = ref([]);

async function fetchFleet() {
  await getFleet(route.params.uid.toString());
}

const onAttachDevicesSidePanelShow = () => {
  attachDevicesPanelShown.value = true;
};

const countDevicesToRemove = ref();
const removeDtwins: Ref<Dtwin[]> = ref([]);

const onConfirmRemoveDevices = async () => {
  if (removeDtwins.value) {
    await onUpdateFleet(FleetUpdateActionType.REMOVE, removeDtwins.value);
  }
  showConfirmRemoveDevices.value = false;
};
const onRemoveDevices = (dtwinList: Dtwin[]) => {
  removeDtwins.value = dtwinList;
  countDevicesToRemove.value = dtwinList.length;
  showConfirmRemoveDevices.value = true;
};

async function onUpdateFleet(
  fleetAction: FleetUpdateActionType,
  dtwins: Dtwin[],
) {
  const selectedDtwinsUids: string[] = dtwins.map((dtwin: Dtwin) => dtwin.uid);
  let fleetUpdatePayload: FleetUpdatePayload;
  switch (fleetAction) {
    case FleetUpdateActionType.REMOVE:
      fleetUpdatePayload = {
        deviceOperations: {
          remove: selectedDtwinsUids,
        },
      };
      break;
    case FleetUpdateActionType.ADD:
    default:
      fleetUpdatePayload = {
        deviceOperations: {
          add: selectedDtwinsUids,
        },
      };
      break;
  }
  if (resultGetFleet.value) {
    await updateFleet(resultGetFleet.value.uid, fleetUpdatePayload);

    if (LxcError.check(errorUpdateFleet.value)) {
      errorUpdateFleet.value.notify(NotificationKey.saveError);
    } else {
      if (fleetAction === FleetUpdateActionType.ADD) {
        showNotificationSuccess(
          t("fleet.attachDevices.updateSuccess", {
            count: selectedDtwinsUids.length,
            fleetName: resultGetFleet.value?.friendlyName,
          }),
        );
      } else {
        showNotificationSuccess(t(NotificationKey.saveSuccess));
      }
      lxcFleetAttachDevicesRef.value.onClose();
      dtwinsListRef.value.onCancelSelection();
      selectedDtwins.value = [];
      reloadDtwins.value = true;
      reloadAttachDtwinsList.value = true;
    }
  }
}

onMounted(fetchFleet);
</script>

<template>
  <!-- The loading state of the view is handled by the dtwin list instead of
  this component as two async calls with two different scope are done at the
  same times. To avoid the display of multiple spinners one after all, this
  component will only be responsible of displaying its error if needed. -->
  <page-component
    :is-loading="isLoadingGetFleet"
    :error="errorGetFleet"
    :name="resultGetFleet?.friendlyName ?? ''"
    should-not-translate
    :back-route="PATH_FLEETS"
  >
    <template #top-right>
      <lxc-button
        v-if="resultGetFleet"
        :icon="ILxcPlus"
        :title="
          isAllowedToManageFleet
            ? t('fleet.addDevices')
            : t('fleet.actionForbidden')
        "
        :disabled="!isAllowedToManageFleet"
        @click="onAttachDevicesSidePanelShow"
      >
        {{ t("fleet.addDevices") }}
      </lxc-button>
    </template>

    <dtwin-list
      ref="dtwinsListRef"
      v-model:reload="reloadDtwins"
      v-model:selected-dtwins="selectedDtwins"
      clickable
      :fleet-uid="route.params.uid.toString()"
      :context="DtwinListDisplayContext.FLEET"
      :columns="[
        DtwinColumns.FRIENDLY_NAME,
        DtwinColumns.TYPE,
        DtwinColumns.SERIAL_NUMBER,
        DtwinColumns.SECTOR,
        DtwinColumns.CONNECTIVITY,
        DtwinColumns.METASTATUS,
        DtwinColumns.STATE,
        DtwinColumns.CREATED_AT,
        DtwinColumns.ACTION,
      ]"
      :is-removing-from-fleet="isLoadingUpdateFleet"
      @remove-from-fleet="onRemoveDevices"
    />
    <fleet-details-attach-devices-side-panel
      v-if="resultGetFleet"
      ref="lxcFleetAttachDevicesRef"
      v-model:attach-devices-panel-shown="attachDevicesPanelShown"
      v-model:reload-dtwin-list="reloadAttachDtwinsList"
      :fleet="resultGetFleet"
      :is-updating-fleet="isLoadingUpdateFleet"
      @add-to-fleet="
        (dtwins: Dtwin[]) => onUpdateFleet(FleetUpdateActionType.ADD, dtwins)
      "
    />
    <confirm-modal
      :is-dialog-visible="showConfirmRemoveDevices"
      :title="
        t('fleet.removeDevices.confirmTitle', { count: countDevicesToRemove })
      "
      :description="
        t('fleet.removeDevices.confirmMessage', {
          count: countDevicesToRemove,
        })
      "
      :ok-label="t('button.confirm')"
      :cancel-label="t('button.cancel')"
      :disabled-confirm="isLoadingUpdateFleet"
      :icon="ILxcLightTrash2"
      icon-color-theme="error"
      color-theme="danger"
      @confirm="onConfirmRemoveDevices"
      @cancel="showConfirmRemoveDevices = false"
    />
  </page-component>
</template>
